// API service functions for users
import api from './client';

export interface User {
  id: number;
  cognito_username: string;
  user_email: string;
  org_id: string;
  is_active: boolean;
  assigned_by: string;
  assigned_at: string;
  created_at: string;
  updated_at: string;
}

export interface UserListResponse {
  items: User[];
  total: number;
}

export interface UserCreate {
  cognito_username: string;
  user_email: string;
  org_id: string;
  is_active?: boolean;
  assigned_by?: string;
}

export interface UserUpdate {
  cognito_username?: string;
  user_email?: string;
  is_active?: boolean;
  assigned_by?: string;
}

export interface UserWithDetails extends User {
  organization: any;
  components: any[];
  stores: any[];
}

export const userApi = {
  // Create user
  create: async (data: UserCreate): Promise<User> => {
    const response = await api.post('/v1/users/', data);
    return response.data;
  },

  // List users - the server filters and paginates, so search reaches every row,
  // not just the ones already loaded in the browser.
  list: async (skip = 0, limit = 25, search = ''): Promise<UserListResponse> => {
    const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
    if (search) params.append('search', search);
    const response = await api.get(`/v1/users/?${params.toString()}`);
    return response.data;
  },

  // List users by organization
  listByOrg: async (orgId: number, skip = 0, limit = 100): Promise<User[]> => {
    const response = await api.get(`/v1/users/org/${orgId}?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  // Get user by ID
  get: async (id: number): Promise<UserWithDetails> => {
    const response = await api.get(`/v1/users/${id}`);
    return response.data;
  },

  // Update user
  update: async (id: number, data: UserUpdate): Promise<User> => {
    const response = await api.put(`/v1/users/${id}`, data);
    return response.data;
  },

  // Disable user
  disable: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/v1/users/${id}`);
    return response.data;
  },

  // Update user component access
  updateComponentAccess: async (userId: number, componentIds: number[]): Promise<{ message: string }> => {
    const response = await api.post(`/v1/users/${userId}/components`, {
      user_id: userId,
      component_ids: componentIds
    });
    return response.data;
  },

  // Add user store access
  addStoreAccess: async (userId: number, access: {
    user_email: string;
    org_id: string;
    store_id: string;
    access_level?: string;
    granted_by?: string;
  }): Promise<any> => {
    const response = await api.post(`/v1/users/${userId}/store-access`, access);
    return response.data;
  },

  // Get user store access
  getStoreAccess: async (userId: number): Promise<any[]> => {
    const response = await api.get(`/v1/users/${userId}/store-access`);
    return response.data;
  },

  // Remove user store access
  removeStoreAccess: async (userId: number, storeId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/v1/users/${userId}/store-access/${storeId}`);
    return response.data;
  },

  // Get user dashboard access
  getDashboardAccess: async (userId: number): Promise<any[]> => {
    const response = await api.get(`/v1/users/${userId}/dashboard-access`);
    return response.data;
  },

  // Add user dashboard access
  addDashboardAccess: async (userId: number, access: {
    user_email: string;
    org_id: string;
    page_id: string;
    component_id: string;
    access_level?: string;
    granted_by?: string;
  }): Promise<any> => {
    const response = await api.post(`/v1/users/${userId}/dashboard-access`, access);
    return response.data;
  },

  // Remove user dashboard access
  removeDashboardAccess: async (userId: number, pageId: string, componentId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/v1/users/${userId}/dashboard-access/${pageId}/${componentId}`);
    return response.data;
  }
};
